package io.jsonize.web.viewmodel

import io.jsonize.web.domain.model.HistoryItem
import io.jsonize.web.compare.LineDiff
import io.jsonize.web.screens.editor.JsonError
import io.jsonize.web.services.PlatformStorage
import io.jsonize.web.worker.JsonWorkerClient
import kotlinx.coroutines.*
import kotlinx.coroutines.flow.*
import kotlinx.serialization.json.Json
import kotlinx.serialization.json.JsonElement
import kotlinx.serialization.json.JsonArray
import kotlinx.serialization.json.JsonObject
import kotlinx.serialization.json.JsonPrimitive
import kotlinx.serialization.decodeFromString
import kotlinx.serialization.encodeToString
import kotlin.random.Random
import kotlin.time.ExperimentalTime

@OptIn(ExperimentalTime::class)
class AppViewModel {

    companion object {
        const val SITE_NAME = "Jsonize I/O"
        const val SITE_DESCRIPTION = ""
        private const val MAX_CONTENT_CHARS = 2 * 1024 * 1024
        private const val AUTO_FORMAT_DELAY_MS = 500L
        private const val HISTORY_STORAGE_KEY = "json_history"
        private const val HISTORY_MAX_ITEMS = 50
        private const val HISTORY_SAVE_DELAY_MS = 1200L
        internal val DEFAULT_JSON_PLACEHOLDER = """
            {
              "title": "Welcome",
              "message": "You can Drop - Text / File (.txt, .json) or Paste Copied Text Here"
            }
        """.trimIndent()
        internal val DEFAULT_JSON_COMPARE_PLACEHOLDER = """
            {
                "title": "Compare Json",
                "message": "Drop - Text / File (.txt, .json) or Paste Copied Text Here to Compare"
            }
        """.trimIndent()
    }

    private val jsonParser = Json { ignoreUnknownKeys = true }
    private val historyJson = Json { encodeDefaults = true; ignoreUnknownKeys = true }

    private val scope = CoroutineScope(SupervisorJob() + Dispatchers.Main)

    private val _text = MutableStateFlow("")
    val text: StateFlow<String> = _text

    private val _error = MutableStateFlow<String?>(null)
    val error: StateFlow<String?> = _error

    private val _jsonError = MutableStateFlow<JsonError?>(null)
    val jsonError: StateFlow<JsonError?> = _jsonError

    private val _formatting = MutableStateFlow(false)
    val formatting: StateFlow<Boolean> = _formatting

    private val _formatProgress = MutableStateFlow(0f)
    val formatProgress: StateFlow<Float> = _formatProgress

    private val _compareText = MutableStateFlow("")
    val compareText: StateFlow<String> = _compareText
    private val _compareLeftDiffs = MutableStateFlow<List<LineDiff>>(emptyList())
    val compareLeftDiffs: StateFlow<List<LineDiff>> = _compareLeftDiffs
    private val _compareRightDiffs = MutableStateFlow<List<LineDiff>>(emptyList())
    val compareRightDiffs: StateFlow<List<LineDiff>> = _compareRightDiffs

    private val _code = MutableStateFlow("")
    val code: StateFlow<String> = _code

    private val _codeFileName = MutableStateFlow("")
    val codeFileName: StateFlow<String> = _codeFileName

    private val _showCodeGeneratorSettings = MutableStateFlow(true)
    val showCodeGeneratorSettings: StateFlow<Boolean> = _showCodeGeneratorSettings

    private var formatJob: Job? = null
    private var compareFormatJob: Job? = null
    private var autoFormatJob: Job? = null

    // --- helpers ---

    // NEW: parsed JSON for Tree / Compare / Generator
    private val _parsedJson = MutableStateFlow<JsonElement?>(null)
    val parsedJson: StateFlow<JsonElement?> = _parsedJson
    private var parseJob: Job? = null

    private val _history = MutableStateFlow<List<HistoryItem>>(emptyList())
    val history: StateFlow<List<HistoryItem>> = _history
    private var historyJob: Job? = null
    private var lastHistoryContent: String? = null
    private var activeHistoryId: String? = null

    init {
        scope.launch { loadHistory() }
    }

    private fun isTooLarge(s: String) = s.length > MAX_CONTENT_CHARS

    private fun tooLargeMessage(s: String): String {
        val kb = s.length / 1024
        return "Content too large (${kb} KB). Max allowed is 2048 KB (2 MB)."
    }

    fun updateError(newError: String) {
        _error.value = newError
    }

    fun clearError() {
        _error.value = null
    }

    fun updateCompareText(text: String) {
        _compareText.value = text
    }

    fun updateCompareDiffs(left: List<LineDiff>, right: List<LineDiff>) {
        _compareLeftDiffs.value = left
        _compareRightDiffs.value = right
    }

    fun clearCompareDiffs() {
        _compareLeftDiffs.value = emptyList()
        _compareRightDiffs.value = emptyList()
    }

    fun formatCompareJsonAsync() {
        formatCompareTextAsync { JsonWorkerClient.format(it) }
    }

    fun minifyCompareJsonAsync() {
        formatCompareTextAsync { JsonWorkerClient.minify(it) }
    }

    private fun formatCompareTextAsync(transform: suspend (String) -> String) {
        val input = _compareText.value
        if (input.isBlank()) return
        if (isTooLarge(input)) return

        compareFormatJob?.cancel()
        compareFormatJob = scope.launch {
            try {
                val result = transform(input)
                _compareText.value = result
            } catch (_: CancellationException) {
                // ignore
            } catch (_: Throwable) {
                // ignore
            }
        }
    }

    fun swapCompareTexts(recordHistory: Boolean = true) {
        val mainText = _text.value
        val compareText = _compareText.value
        if (mainText == compareText) return

        updateText(compareText, autoFormat = false, recordHistory = recordHistory)
        if (_text.value != compareText) return

        _compareText.value = mainText
        clearCompareDiffs()
    }

    fun updateText(
        newText: String,
        autoFormat: Boolean = false,
        recordHistory: Boolean = true
    ) {
        if (isTooLarge(newText)) {
            _error.value = tooLargeMessage(newText)
            return
        }
        setText(newText)

        if (autoFormat) {
            scheduleAutoFormat(newText)
        } else {
            autoFormatJob?.cancel()
        }

        if (recordHistory) {
            scheduleHistorySave(newText)
        } else {
            historyJob?.cancel()
        }
    }

    private fun setText(newText: String) {
        _text.value = newText
        triggerParse(newText)
    }

    fun updateCode(code: String, fileName: String){
        _code.update { code }
        _codeFileName.update { fileName }
    }

    private fun scheduleAutoFormat(expectedText: String) {
        autoFormatJob?.cancel()

        if (expectedText.isBlank()) return
        if (isTooLarge(expectedText)) return

        autoFormatJob = scope.launch {
            delay(AUTO_FORMAT_DELAY_MS)
            if (_text.value != expectedText) return@launch
            if (_formatting.value) return@launch

            val formatted = try {
                JsonWorkerClient.format(expectedText)
            } catch (_: Throwable) {
                return@launch
            }

            if (formatted != expectedText) {
                setText(formatted)
            }
        }
    }

    private fun scheduleHistorySave(expectedText: String) {
        historyJob?.cancel()

        if (expectedText.isBlank()) return
        if (isTooLarge(expectedText)) return

        historyJob = scope.launch {
            delay(HISTORY_SAVE_DELAY_MS)
            if (_text.value != expectedText) return@launch

            val element = try {
                jsonParser.parseToJsonElement(expectedText)
            } catch (_: Throwable) {
                return@launch
            }

            if (lastHistoryContent == expectedText) return@launch

            val currentId = activeHistoryId
            if (currentId != null) {
                val existing = _history.value.firstOrNull { it.id == currentId }
                if (existing != null) {
                    val baseTitle = deriveTitle(element)
                    val uniqueTitle = ensureUniqueTitle(baseTitle, _history.value, currentId)
                    val updatedItem = existing.copy(
                        title = uniqueTitle,
                        timestamp = kotlin.time.Clock.System.now(),
                        content = expectedText
                    )
                    val updatedList = listOf(updatedItem) + _history.value.filter { it.id != currentId }
                    _history.value = updatedList
                    lastHistoryContent = expectedText
                    persistHistory(updatedList)
                    return@launch
                } else {
                    activeHistoryId = null
                }
            }

            val baseTitle = deriveTitle(element)
            val uniqueTitle = ensureUniqueTitle(baseTitle, _history.value)
            val item = HistoryItem(
                id = Random.nextLong().toString(),
                title = uniqueTitle,
                timestamp = kotlin.time.Clock.System.now(),
                content = expectedText
            )

            val updated = listOf(item) + _history.value.filter { it.content != expectedText }
            val trimmed = updated.take(HISTORY_MAX_ITEMS)
            _history.value = trimmed
            lastHistoryContent = expectedText
            activeHistoryId = item.id
            persistHistory(trimmed)
        }
    }

    fun applyHistoryItem(item: HistoryItem) {
        activeHistoryId = item.id
        lastHistoryContent = item.content
        updateText(item.content, autoFormat = false, recordHistory = false)
    }

    fun deleteHistoryItem(id: String) {
        val updated = _history.value.filterNot { it.id == id }
        _history.value = updated
        if (activeHistoryId == id) {
            activeHistoryId = null
        }
        if (updated.isEmpty()) {
            lastHistoryContent = null
        }
        scope.launch { persistHistory(updated) }
    }

    fun clearHistory() {
        _history.value = emptyList()
        lastHistoryContent = null
        activeHistoryId = null
        scope.launch { persistHistory(emptyList()) }
    }

    private suspend fun loadHistory() {
        val storage = storageOrNull() ?: return
        val raw = storage.load(HISTORY_STORAGE_KEY) ?: return
        val items = try {
            historyJson.decodeFromString<List<HistoryItem>>(raw)
        } catch (_: Throwable) {
            emptyList()
        }
        _history.value = items
        lastHistoryContent = items.firstOrNull()?.content
    }

    private suspend fun persistHistory(items: List<HistoryItem>) {
        val storage = storageOrNull() ?: return
        val encoded = historyJson.encodeToString(items)
        storage.save(HISTORY_STORAGE_KEY, encoded)
    }

    private fun deriveTitle(element: JsonElement): String {
        return when (element) {
            is JsonObject -> {
                val preferred = listOf("title", "name", "id", "type", "key")
                preferred.forEach { key ->
                    val value = element[key]
                    if (value is JsonPrimitive) {
                        return primitiveTitleValue(value)
                    }
                }

                val first = element.entries.firstOrNull()
                if (first != null) {
                    val value = first.value
                    if (value is JsonPrimitive) {
                        return "${first.key}: ${primitiveTitleValue(value)}"
                    }
                    return first.key
                }
                "Object"
            }
            is JsonArray -> {
                val size = element.size
                if (size == 0) return "Array (0)"
                val firstTitle = deriveTitle(element.first())
                "Array ($size) $firstTitle"
            }
            is JsonPrimitive -> primitiveTitleValue(element)
        }
    }

    private fun ensureUniqueTitle(
        base: String,
        existing: List<HistoryItem>,
        ignoreId: String? = null
    ): String {
        val normalized = base.trim().ifEmpty { "Untitled" }
        if (existing.none { it.title == normalized && it.id != ignoreId }) return normalized

        var idx = 2
        var candidate: String
        do {
            candidate = "$normalized ($idx)"
            idx++
        } while (existing.any { it.title == candidate && it.id != ignoreId })

        return candidate
    }

    private fun primitiveTitleValue(value: JsonPrimitive): String {
        return if (value.isString) value.content else value.toString()
    }

    private fun storageOrNull(): PlatformStorage? {
        return runCatching { PlatformStorage.default }.getOrNull()
    }


    fun cancelFormatting() {
        formatJob?.cancel()
        _formatting.value = false
        _formatProgress.value = 0f
    }

    private fun triggerParse(text: String) {
        parseJob?.cancel()

        if (text.isBlank()) {
            _parsedJson.value = null
            return
        }

        if (isTooLarge(text)) {
            _parsedJson.value = null
            return
        }

        parseJob = scope.launch(Dispatchers.Default) {
            try {
                val element = jsonParser.parseToJsonElement(text)
                withContext(Dispatchers.Main) {
                    _parsedJson.value = element
                    // clear only "invalid json" type errors
                    if (_error.value?.startsWith("Invalid JSON") == true) {
                        _error.value = null
                    }
                }
            } catch (e: Throwable) {
                // Keep last valid JSON in Kotlin state; CodeMirror handles error display.
            }
        }
    }

    // --- format using Web Worker ---

    fun formatJsonAsync() {
        val input = _text.value

        if (isTooLarge(input)) {
            _error.value = tooLargeMessage(input)
            return
        }

        formatJob?.cancel()
        _formatting.value = true
        _formatProgress.value = 0f

        formatJob = scope.launch {
            try {
                // worker runs off main thread; await is non-blocking for UI
                val result = JsonWorkerClient.format(input)
                _text.value = result
                _error.value = null
            } catch (e: CancellationException) {
                // ignore
            } catch (e: Throwable) {
                _error.value = e.message ?: "Formatting failed"
            } finally {
                _formatting.value = false
                _formatProgress.value = 0f
            }
        }
    }

    // --- minify using Web Worker ---

    fun minifyJsonAsync() {
        val input = _text.value

        if (isTooLarge(input)) {
            _error.value = tooLargeMessage(input)
            return
        }

        formatJob?.cancel()
        _formatting.value = true
        _formatProgress.value = 0f

        formatJob = scope.launch {
            try {
                val result = JsonWorkerClient.minify(input)
                _text.value = result
                _error.value = null
            } catch (e: Throwable) {
                _error.value = e.message ?: "Minify failed"
            } finally {
                _formatting.value = false
                _formatProgress.value = 0f
            }
        }
    }

    // ---------- NEW: async JSON validation via worker ----------
    fun validateJsonAsync() {
        val input = _text.value

        if (input.isBlank()) {
            _jsonError.value = null
            // keep _error as custom/other errors
            return
        }
        if (isTooLarge(input)) {
            _jsonError.value = null
            _error.value = tooLargeMessage(input)
            return
        }

        scope.launch {
            try {
                val res = JsonWorkerClient.validate(input)
                if (res.valid) {
                    _jsonError.value = null
                    // don't overwrite other non-JSON errors
                    if (_error.value?.startsWith("Invalid JSON") == true) {
                        _error.value = null
                    }
                } else {
                    val message = res.message ?: "Invalid JSON"
                    val jsonErr = JsonError(
                        message = message,
                        line = res.line,
                        column = res.column,
                        offset = res.offset
                    )
                    _jsonError.value = jsonErr
                }
            } catch (e: Throwable) {
                val msg = e.message ?: "JSON validation failed"
                _jsonError.value = JsonError(msg)
            }
        }
    }

    fun updateShowSettings(show: Boolean) {
        _showCodeGeneratorSettings.update {
            show
        }
    }
}
